import { useState } from "react";

const articles = [
  {
    id: 1,
    category: "Foundation",
    title: "When the Face Holds What the Body Cannot Release",
    excerpt:
      "The face is not merely a surface. It is a living record of what the body has carried — and what it has not yet been able to let go.",
    body: `Stress does not always feel intense.

More often, it becomes low-level, ongoing, and easy to overlook. Function remains intact. Daily responsibilities are still managed. From the outside, very little appears disrupted.

But physiologically, the system may not have returned to baseline.

When the nervous system stays in prolonged low-grade activation, the body does not resolve the load. It retains it—not as a single event, but as a repeated internal state.

Over time, this organizes into patterns.

One of the earliest places this becomes visible is the face.
Not because it is the cause, but because it is structurally sensitive to internal change. A jaw that does not fully release. A forehead that holds at rest. A face that feels heavier or less responsive.

These are rarely random.
They reflect how the system is holding tension.

At this stage, three shifts are already in place:
Less definition. Less responsiveness. Less coherence.

Microtension becomes baseline.
Repeated contraction in the jaw, brow, and neck is no longer temporary. The system stops distinguishing between activation and rest.
Circulation becomes inefficient. Reduced blood flow and lymphatic movement may not be obvious, but they affect clarity, recovery, and fluid balance.
Tissue adapts to holding. Fascia reorganizes around tension, reinforcing the same pattern. The face reflects the load it carries. 

These changes are gradual, which is why they are often overlooked. What follows is not dramatic but cumulative—a shift in presence rather than structure. This is usually when correction is introduced.

Most aesthetic approaches focus on visible outcomes: lifting, tightening, and smoothing. They can create change, but they do not address the conditions that produced it.

If the regulatory state remains unchanged, the pattern returns. Because the issue is not limited to structure.
It is linked to how the system is functioning.

Within the Stability framework, the face is approached as a system output—shaped by nervous system load, circulation, and structural tension.

When regulation improves, the shift is not forced.
Tension reduces.
Circulation restores consistency.
Tissue becomes responsive again.

The change is subtle but precise:
The face no longer reflects compensation.
It reflects regulation.

Facial aging is often described as decline.

In many cases, it is adaptation—
to load that has not been released,
and a system that has not returned to balance.`,
    date: "Jan 01, 2026",
    readTime: "6 min read",
    image:
      "https://public.readdy.ai/ai/img_res/c8f51b67e88ad1646551792ef736614e.jpg",
  },
  {
    id: 2,
    category: "Foundation",
    title: "Beyond Skin: Why Most Facial Changes Are Not Surface-Level",
    excerpt:
      "The most significant changes in the face do not begin at the skin. They begin far beneath it — in the bone, the muscle, the fascia, and the systems that regulate them all.",
    body: `The most significant changes in the face do not begin at the skin. They begin far beneath it — in the bone, the muscle, the fascia, and the systems that regulate them all.

This is one of the most important — and most overlooked — principles in facial care. The skincare industry has long focused on the epidermis: hydration, barrier function, collagen stimulation at the surface. These are not without value. But they address only the outermost layer of a deeply layered system.

Beneath the skin lies the dermis, with its collagen and elastin networks. Beneath that, the subcutaneous fat compartments — which shift, migrate, and diminish with age. Deeper still, the facial muscles — which both animate the face and, when chronically contracted, reshape it. And beneath the muscles, the periosteum and bone — the foundational scaffold upon which everything else rests.

Changes at any of these layers will eventually be reflected at the surface. Bone resorption alters the angles of the face. Muscular imbalance creates asymmetry and accelerates soft tissue descent. Fascial restriction limits movement and impairs circulation. Fat compartment migration creates hollowing in some areas and heaviness in others.

Addressing these changes requires working at the level where they originate. The Vionne Stability Method is designed to do exactly this — engaging each layer of the facial system with precision, and creating conditions for change that is structural, not merely superficial.`,
    date: "Jan 15, 2026",
    readTime: "7 min read",
    image:
      "https://readdy.ai/api/search-image?query=Clean%20Japanese%20minimalist%20study%20desk%20with%20warm%20oak%20wood%20surface%2C%20open%20anatomy%20reference%20book%20showing%20layered%20facial%20tissue%20cross-section%20diagrams%2C%20soft%20warm%20natural%20window%20light%2C%20simple%20ceramic%20cup%2C%20cream%20paper%20and%20fine%20pencil%2C%20warm%20natural%20wood%20and%20ivory%20tones%2C%20focused%20scholarly%20atmosphere%2C%20bright%20airy%20editorial%20flat%20lay%20photography%20with%20sophisticated%20Japanese%20aesthetic&width=800&height=560&seq=edu-blog-b2&orientation=landscape",
  },
  {
    id: 3,
    category: "Nervous System",
    title: "The Face as a Reflection of Emotional Load",
    excerpt:
      "Emotions do not simply pass through us. They leave traces — in the nervous system, in the fascia, and in the face that the world sees.",
    body: `Emotions do not simply pass through us. They leave traces — in the nervous system, in the fascia, and in the face that the world sees.

This is not a metaphor. It is physiology. The facial muscles are among the most directly connected to the emotional processing centres of the brain. The amygdala, the anterior cingulate cortex, the insula — these structures not only process emotional experience, they generate motor responses in the face. We are wired to express what we feel.

But what happens when expression is suppressed? When grief is held back, when anger is swallowed, when anxiety is managed rather than resolved? The impulse to express does not disappear. It is redirected — into the tissues. The jaw tightens. The throat constricts. The brow holds a subtle tension that never fully releases.

Over years, these suppressed expressions become structural. The fascia adapts to the holding pattern. The muscles shorten and lose their full range of motion. The face begins to reflect not just the emotions of the present moment, but the accumulated emotional history of a lifetime.

In clinical practice, this is visible. Clients who have experienced prolonged grief often present with a particular quality of heaviness around the eyes and cheeks. Those who carry chronic anxiety frequently show tension in the jaw, the temples, and the neck. These are not coincidences — they are the face's faithful record of what the body has been asked to hold.

The Vionne Stability Method acknowledges this dimension of facial change. Treatment is designed not only to release physical tension, but to create the conditions in which the nervous system can begin to process and release what has been stored.`,
    date: "Feb 01, 2026",
    readTime: "8 min read",
    image:
      "https://readdy.ai/api/search-image?query=Tranquil%20Japanese%20minimalist%20meditation%20corner%20with%20warm%20natural%20light%2C%20simple%20light%20oak%20wood%20low%20table%20with%20open%20wellness%20journal%20showing%20hand-drawn%20emotional%20mapping%20sketches%2C%20dried%20botanicals%20in%20simple%20ceramic%20vessel%2C%20soft%20cream%20and%20warm%20sand%20tones%2C%20peaceful%20contemplative%20atmosphere%2C%20refined%20editorial%20interior%20photography%20with%20Japanese%20wabi-sabi%20aesthetic&width=800&height=560&seq=edu-blog-b3&orientation=landscape",
  },
  {
    id: 4,
    category: "Nervous System",
    title: "Tension Patterns: How Stress Reshapes Your Face",
    excerpt:
      "Stress does not age the face uniformly. It follows patterns — predictable, identifiable, and, with the right approach, addressable.",
    body: `Stress does not age the face uniformly. It follows patterns — predictable, identifiable, and, with the right approach, addressable.

The relationship between stress and facial change is well established in clinical observation, though less often discussed in aesthetic practice. Chronic stress activates the hypothalamic-pituitary-adrenal axis, elevating cortisol and triggering a cascade of physiological effects: increased inflammation, impaired collagen synthesis, reduced skin barrier function, and altered blood flow to peripheral tissues.

But beyond these systemic effects, stress creates specific muscular and fascial holding patterns that directly reshape the face over time.

The most common pattern involves the jaw and temporal region. Chronic stress activates the masseter, temporalis, and pterygoid muscles — the muscles of mastication — even in the absence of chewing. This sustained contraction creates visible changes: a squaring of the lower face, temporal hollowing, and a quality of tightness that affects the entire mid and lower face.

A second common pattern involves the frontalis and corrugator muscles — the muscles of the forehead and brow. Habitual worry, concentration, or emotional suppression creates chronic activation of these muscles, resulting in the characteristic lines and heaviness of the upper face.

A third pattern involves the neck and suboccipital region. Postural stress, screen use, and the physical weight of chronic vigilance create tension that travels upward into the face, affecting jaw position, lymphatic drainage, and the overall quality of facial tone.

Identifying which patterns are present — and understanding their systemic origins — is the foundation of effective treatment within the Vionne Stability Method.`,
    date: "Feb 15, 2026",
    readTime: "7 min read",
    image:
      "https://readdy.ai/api/search-image?query=Minimalist%20Japanese%20workspace%20with%20warm%20natural%20light%2C%20light%20oak%20wood%20desk%20surface%20with%20open%20sketchbook%20showing%20detailed%20facial%20muscle%20tension%20mapping%20diagrams%2C%20simple%20ceramic%20tea%20bowl%20with%20green%20tea%2C%20bamboo%20pen%20holder%2C%20warm%20ivory%20and%20natural%20wood%20tones%2C%20focused%20scholarly%20atmosphere%2C%20bright%20airy%20editorial%20photography%20with%20refined%20Japanese%20aesthetic&width=800&height=560&seq=edu-blog-b4&orientation=landscape",
  },
  {
    id: 5,
    category: "Structure",
    title: "The Hidden Architecture of Facial Change",
    excerpt:
      "Beneath every visible change in the face lies a structural story — one written in bone, muscle, and fascia long before it becomes visible at the surface.",
    body: `Beneath every visible change in the face lies a structural story — one written in bone, muscle, and fascia long before it becomes visible at the surface.

The face is a three-dimensional structure, and its appearance at any given moment is the result of the relationship between its layers: the bony scaffold, the muscular system, the fascial network, the fat compartments, and the skin. When we speak of facial ageing, we are really speaking of changes in these relationships — shifts in volume, position, and tension that alter the face's overall geometry.

Bone is the foundation. The facial skeleton undergoes continuous remodelling throughout life, and the changes are not uniform. The orbital rim recedes, enlarging the apparent size of the eye socket. The midface loses volume, reducing the projection of the cheekbones. The mandible resorbs, particularly in the chin and angle regions, altering the proportions of the lower face. These skeletal changes are gradual and largely invisible — until the soft tissue above them begins to reflect the loss of support.

The muscular system responds to these skeletal changes, as well as to the patterns of use and disuse that accumulate over a lifetime. Muscles that are chronically overused shorten and thicken. Muscles that are underused atrophy. The result is a face that is structurally imbalanced — with areas of excess tension and areas of insufficient support.

The fascial network connects all of these layers, transmitting forces across the face and maintaining the spatial relationships between structures. When fascia becomes restricted — through tension, inflammation, or the cumulative effects of habitual expression — it limits movement, impairs circulation, and contributes to the visible changes we associate with ageing.

Understanding this architecture is the first step toward addressing it intelligently.`,
    date: "Mar 01, 2026",
    readTime: "8 min read",
    image:
      "https://readdy.ai/api/search-image?query=Elegant%20Japanese%20minimalist%20study%20space%20with%20warm%20light%20oak%20wood%20desk%2C%20open%20anatomy%20book%20showing%20detailed%20skeletal%20facial%20structure%20and%20architectural%20diagrams%2C%20soft%20warm%20natural%20daylight%20from%20large%20window%2C%20simple%20ceramic%20tea%20cup%2C%20cream%20linen%20notebook%2C%20warm%20natural%20wood%20and%20cream%20tones%2C%20zen%20peaceful%20scholarly%20atmosphere%2C%20bright%20airy%20editorial%20interior%20photography%20with%20sophisticated%20Japanese%20aesthetic&width=800&height=560&seq=edu-blog-b5&orientation=landscape",
  },
  {
    id: 6,
    category: "Circulation",
    title: "Flow & Stagnation: Why Circulation Defines Your Skin Quality",
    excerpt:
      "The quality of your skin is, in large part, the quality of your circulation. Where blood and lymph flow freely, the skin is vital. Where they stagnate, the skin reflects it.",
    body: `The quality of your skin is, in large part, the quality of your circulation. Where blood and lymph flow freely, the skin is vital. Where they stagnate, the skin reflects it.

This principle is central to both traditional Chinese medicine and contemporary dermatology, though it is expressed in different languages. In TCM, the concept of qi and blood stagnation describes a state in which the vital substances of the body are not moving freely — resulting in dullness, discolouration, puffiness, and a loss of the luminosity that characterises healthy skin. In Western physiology, the same phenomenon is described in terms of microcirculation, lymphatic drainage, and the delivery of oxygen and nutrients to the skin's cellular layers.

The face has a particularly rich vascular and lymphatic supply, and it is exquisitely sensitive to changes in circulatory function. When microcirculation is impaired — by tension, inflammation, cold, or systemic dysregulation — the skin loses its warmth and colour. Cellular repair slows. The production of collagen and elastin is reduced. The skin becomes dull, uneven, and less resilient.

Lymphatic stagnation compounds these effects. The lymphatic system is responsible for removing metabolic waste, excess fluid, and inflammatory byproducts from the tissues. When lymph flow is sluggish — as it frequently is in the face, particularly around the eyes, cheeks, and jaw — the tissues become congested. The face appears heavy, puffy, or tired, regardless of how much rest one has had.

Restoring circulatory flow is one of the most immediately visible aspects of the Vionne Stability Method. Clients frequently notice a brightening of skin tone, a reduction in puffiness, and a quality of aliveness in the face following treatment — the direct result of improved fluid movement through the tissues.`,
    date: "Mar 15, 2026",
    readTime: "6 min read",
    image:
      "https://readdy.ai/api/search-image?query=Traditional%20Japanese%20minimalist%20still%20life%20with%20light%20oak%20wood%20table%2C%20botanical%20herbs%20and%20dried%20flowers%20arranged%20beside%20open%20wellness%20journal%20showing%20circulation%20flow%20diagrams%2C%20soft%20warm%20natural%20daylight%2C%20simple%20ceramic%20vessels%20with%20plant%20extracts%2C%20warm%20natural%20wood%20and%20cream%20tones%2C%20zen%20scholarly%20atmosphere%2C%20bright%20airy%20editorial%20still%20life%20photography%20with%20sophisticated%20Japanese%20aesthetic&width=800&height=560&seq=edu-blog-b6&orientation=landscape",
  },
  {
    id: 7,
    category: "Seasonal",
    title: "Spring Rebalancing: Awakening Circulation & Liver Regulation",
    excerpt:
      "Spring is not merely a change in weather. In the body, it is a season of movement — a time when the systems of circulation, detoxification, and renewal naturally seek to reactivate.",
    body: `Spring is not merely a change in weather. In the body, it is a season of movement — a time when the systems of circulation, detoxification, and renewal naturally seek to reactivate.

In traditional Chinese medicine, spring is associated with the liver and gallbladder — the organ systems responsible for the smooth flow of qi and blood throughout the body. When these systems are functioning well, energy moves freely, emotions are regulated, and the face reflects a quality of clarity and vitality. When they are congested or dysregulated, the effects are visible: dullness, puffiness, tension around the eyes and temples, and a general heaviness that persists despite adequate rest.

The transition from winter to spring is a particularly important time for facial regulation. During winter, the body naturally contracts — circulation slows, the lymphatic system becomes more sluggish, and the face often reflects this inward movement with increased puffiness, reduced tone, and a loss of luminosity. As spring arrives, the body's natural impulse is to move outward again — to release what has accumulated and restore the free flow of vital substances.

Supporting this seasonal transition through targeted treatment can have a profound effect on the face. Techniques that stimulate lymphatic drainage, activate microcirculation, and release the fascial holding patterns that have accumulated over winter create the conditions for genuine renewal — not just at the surface, but throughout the facial system.

Dietary and lifestyle adjustments also play a role. Foods that support liver function — bitter greens, fermented vegetables, lemon — help to clear the stagnation that accumulates over winter. Reducing alcohol, processed foods, and excessive screen time supports the nervous system's transition toward a more regulated state.

Spring rebalancing is not a single treatment. It is a seasonal practice — a conscious alignment with the body's natural rhythms that, over time, creates a face that reflects genuine vitality rather than the accumulated weight of what has not been released.`,
    date: "Mar 15, 2026",
    readTime: "9 min read",
    image:
      "https://readdy.ai/api/search-image?query=Refined%20Japanese%20minimalist%20spring%20scene%20with%20warm%20natural%20light%2C%20light%20oak%20wood%20surface%20with%20fresh%20botanical%20sprigs%20and%20open%20journal%20showing%20seasonal%20wellness%20notes%2C%20delicate%20dried%20flowers%20and%20green%20herbs%20in%20simple%20ceramic%20vase%2C%20soft%20cream%20and%20warm%20sage%20tones%2C%20zen%20peaceful%20spring%20atmosphere%2C%20bright%20airy%20editorial%20still%20life%20photography%20with%20sophisticated%20Japanese%20aesthetic&width=800&height=560&seq=edu-blog-b7&orientation=landscape",
  },
];

type ActiveSection = "journal" | "masterclass" | "training";

interface BlogSectionProps {
  activeSection: ActiveSection;
  setActiveSection: (section: ActiveSection) => void;
}

const tabs = [
  { key: "journal" as ActiveSection, label: "Stability Journal Insights" },
  { key: "masterclass" as ActiveSection, label: "Stability Masterclass" },
  { key: "training" as ActiveSection, label: "Method Training Program" },
];

/* ─── Masterclass inline content ─── */
const MasterclassContent = () => (
  <div style={{ animation: "fadeSlideIn 0.4s ease-out" }}>
    <div className="inline-flex items-center gap-2.5 mb-8">
      <div className="w-10 h-[1px] bg-bronze/40" />
      <span className="text-bronze text-[9px] font-sans font-semibold tracking-[0.35em] uppercase">
        Self-guided learning
      </span>
      <div className="w-10 h-[1px] bg-bronze/40" />
    </div>
    <h2 className="font-display text-4xl md:text-5xl font-light text-charcoal italic leading-snug mb-3">
      Stability Masterclass Series
    </h2>
    <h3 className="font-sans text-base font-semibold tracking-[0.2em] uppercase text-textGray/70 mb-10">
      Learning to Understand Your Own Skin
    </h3>
    <div className="w-16 h-[1px] bg-bronze/30 mb-10" />
    <p className="text-textGray/70 text-[13px] font-sans font-light leading-[2] mb-6">
      A collection of in-depth studies exploring the hidden dynamics behind facial change and skin stability.
    </p>
    <p className="text-textGray/70 text-[13px] font-sans font-light leading-[2] mb-6">
      Designed for individuals who wish to deepen their understanding of facial regulation, these courses provide practical knowledge on how to care for the skin between treatments and how lifestyle patterns influence long-term skin stability.
    </p>
    <p className="text-textGray/70 text-[13px] font-sans font-light leading-[2] mb-10">
      These masterclasses are designed for individuals who wish to understand the deeper relationship between internal regulation, lifestyle rhythms, and the evolving landscape of the face.
    </p>
    <p className="text-textGray/70 text-[13px] font-sans font-light leading-[2] mb-10">
      These programs are suitable for individuals with a general interest in skincare, wellness, and holistic health. They are not intended as professional training for practitioners.
    </p>
    <div className="w-16 h-[1px] bg-bronze/30 mb-10" />
    <p className="text-textGray/50 text-[12px] font-sans font-light leading-relaxed mb-8">
      The Self-Study Series is currently under development and will be available in a future release.
    </p>
    <div className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-bronze/8 border border-bronze/20">
      <i className="ri-time-line text-bronze text-xs"></i>
      <span className="text-bronze text-[10px] font-sans font-semibold tracking-[0.25em] uppercase">
        Coming Soon
      </span>
    </div>
  </div>
);

/* ─── Training inline content ─── */
const TrainingContent = () => {
  return (
    <div style={{ animation: "fadeSlideIn 0.4s ease-out" }}>
      {/* Program info */}
      <div className="inline-flex items-center gap-2.5 mb-8">
        <div className="w-10 h-[1px] bg-bronze/40" />
        <span className="text-bronze text-[9px] font-sans font-semibold tracking-[0.35em] uppercase">
          Practitioner training
        </span>
        <div className="w-10 h-[1px] bg-bronze/40" />
      </div>
      <h2 className="font-display text-4xl md:text-5xl font-light text-charcoal italic leading-snug mb-3">
        Vionne Stability Method
      </h2>
      <h3 className="font-sans text-base font-semibold tracking-[0.2em] uppercase text-textGray/70 mb-10">
        Professional Training
      </h3>
      <p className="text-textGray/70 text-[13px] font-sans font-light leading-[2] mb-6">
        For practitioners seeking a deeper understanding of facial regulation.
      </p>
      <p className="text-textGray/70 text-[13px] font-sans font-light leading-[2] mb-6">
        The Vionne Stability Method is an integrative clinical system that combines Traditional Chinese Medicine diagnostics, botanical therapeutics, and advanced aesthetic understanding to address facial change at its roots.
      </p>
      <p className="text-textGray/70 text-[13px] font-sans font-light leading-[2] mb-10">
        The training program is designed for licensed practitioners who wish to expand their clinical perspective and incorporate facial regulation principles into their existing practice.
      </p>
      <div className="w-16 h-[1px] bg-bronze/30 mb-10" />
      <p className="text-charcoal text-[11px] font-sans font-semibold tracking-[0.25em] uppercase mb-6">
        Participants will learn:
      </p>
      <ul className="space-y-4 mb-14">
        {[
          "The Vionne Stability Framework",
          "Facial regulatory layers and systemic influences",
          "Stability Facial Archetypes diagnostic system",
          "Treatment design for complex facial patterns",
          "Integrative use of acupuncture, botanical therapies, and aesthetic techniques",
        ].map((item, i) => (
          <li key={i} className="flex items-start gap-3">
            <span className="mt-[5px] w-1 h-1 rounded-full bg-bronze flex-shrink-0" />
            <span className="text-textGray/70 text-[13px] font-sans font-light leading-relaxed">{item}</span>
          </li>
        ))}
      </ul>
      <div className="w-full h-[1px] bg-warmGray/20 mb-10" />
      <p className="text-textGray/50 text-[12px] font-sans font-light leading-relaxed mb-8">
        This professional training program is currently under development and will be introduced in a future release.
      </p>
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5 mb-4">
        <div className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-bronze/8 border border-bronze/20">
          <i className="ri-time-line text-bronze text-xs"></i>
          <span className="text-bronze text-[10px] font-sans font-semibold tracking-[0.25em] uppercase">
            Coming Soon
          </span>
        </div>
        <button
          onClick={() => {
            const el = document.getElementById("education-inquiry-section");
            if (el) el.scrollIntoView({ behavior: "smooth" });
          }}
          className="inline-flex items-center gap-2.5 px-7 py-3 bg-charcoal/10 text-charcoal border border-charcoal/20 rounded-full text-[10px] font-sans font-semibold tracking-wider uppercase hover:bg-charcoal hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer"
        >
          <i className="ri-mail-line text-xs"></i>
          <span>Get in Touch</span>
        </button>
      </div>
    </div>
  );
};

/* ─── Main BlogSection ─── */
const BlogSection = ({ activeSection, setActiveSection }: BlogSectionProps) => {
  const [activeId, setActiveId] = useState<number>(1);
  const activeArticle = articles.find((a) => a.id === activeId)!;

  return (
    <section id="insights-journal" className="py-12 md:py-20 px-4 md:px-6 lg:px-12 bg-warmWhite overflow-x-hidden">
      <div className="max-w-6xl mx-auto w-full">

        {/* Section header */}
        <div className="mb-8 md:mb-10">
          <div className="flex items-center gap-4 md:gap-6 mb-5">
            <div className="flex-1 h-[1px] bg-warmGray/20" />
            <span className="text-bronze text-[8px] font-sans font-semibold tracking-[0.35em] md:tracking-[0.45em] uppercase whitespace-nowrap">
              Education &amp; Learning
            </span>
            <div className="flex-1 h-[1px] bg-warmGray/20" />
          </div>
          <p className="text-center font-display text-[14px] md:text-[16px] font-light italic text-charcoal tracking-wide leading-relaxed">
            Where knowledge becomes clarity, and clarity becomes regulation.
          </p>
        </div>

        {/* Tab bar — desktop: centered flex, mobile: horizontal scroll */}
        <div className="mb-10 md:mb-16 border-b border-warmGray/20">
          <div className="flex items-end gap-0 overflow-x-auto scrollbar-hide md:justify-center">
            {tabs.map((tab, index) => {
              const isActive = activeSection === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveSection(tab.key)}
                  className={`relative flex flex-col items-center gap-1.5 px-5 md:px-10 pb-4 md:pb-5 pt-2 transition-all duration-300 cursor-pointer group flex-shrink-0`}
                >
                  <span className={`text-[7px] md:text-[8px] font-sans font-semibold tracking-[0.3em] uppercase transition-colors duration-300 ${isActive ? "text-bronze" : "text-textGray/30 group-hover:text-bronze/50"}`}>
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <span className={`text-[9px] md:text-[10px] font-sans font-semibold tracking-[0.15em] md:tracking-[0.18em] uppercase transition-colors duration-300 whitespace-nowrap ${isActive ? "text-charcoal" : "text-textGray/50 group-hover:text-charcoal/70"}`}>
                    {tab.label}
                  </span>
                  <span className={`absolute bottom-0 left-0 right-0 h-[1.5px] transition-all duration-300 ${isActive ? "bg-charcoal" : "bg-transparent group-hover:bg-warmGray/30"}`} />
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Journal ── */}
        {activeSection === "journal" && (
          <div style={{ animation: "fadeSlideIn 0.4s ease-out" }}>
            <div className="text-center mb-8 md:mb-14">
              <div className="inline-flex items-center gap-2.5 mb-4">
                <div className="w-10 h-[1px] bg-bronze/40" />
                <span className="text-bronze text-[9px] font-sans font-semibold tracking-[0.35em] uppercase">
                  Insights
                </span>
                <div className="w-10 h-[1px] bg-bronze/40" />
              </div>
              <h3 className="font-display text-2xl md:text-[2rem] font-light text-charcoal italic leading-snug">
                Stability Journal
              </h3>
              <p className="text-textGray/60 text-[12px] font-sans font-light leading-relaxed mt-4 max-w-md mx-auto">
                Short reflections and clinical perspectives exploring the deeper dynamics of facial change.
                These writings expand upon the philosophy behind the Vionne Stability Method.
              </p>
            </div>

            {/* ── Mobile layout: stacked ── */}
            <div className="block md:hidden">
              {/* Article selector — horizontal scroll cards */}
              <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-3 mb-6 -mx-4 px-4">
                {articles.map((article, index) => {
                  const isActive = activeId === article.id;
                  return (
                    <button
                      key={article.id}
                      onClick={() => setActiveId(article.id)}
                      className={`flex-shrink-0 w-[160px] text-left px-3.5 py-3.5 rounded-xl transition-all duration-300 cursor-pointer border ${
                        isActive
                          ? "bg-cream border-bronze/25 shadow-sm"
                          : "bg-white/60 border-warmGray/15"
                      }`}
                    >
                      <span className={`block text-[7px] font-sans font-semibold tracking-[0.3em] uppercase mb-1 ${isActive ? "text-bronze" : "text-textGray/30"}`}>
                        {String(index + 1).padStart(2, "0")} · {article.category}
                      </span>
                      <span className={`block font-display text-[12px] font-light italic leading-snug ${isActive ? "text-charcoal" : "text-textGray/55"}`}>
                        {article.title}
                      </span>
                      <span className={`block text-[9px] font-sans mt-1.5 ${isActive ? "text-textGray/50" : "text-textGray/30"}`}>
                        {article.readTime}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Article content */}
              <div key={activeId} style={{ animation: "fadeSlideIn 0.4s ease-out" }}>
                <div className="w-full h-[200px] overflow-hidden rounded-xl mb-5">
                  <img
                    src={activeArticle.image}
                    alt={activeArticle.title}
                    title={`${activeArticle.title} - Vionne Stability`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <span className="px-2.5 py-1 rounded-full text-[7px] font-sans font-semibold tracking-wider uppercase bg-bronze/10 text-bronze border border-bronze/20">
                    {activeArticle.category}
                  </span>
                  <span className="text-textGray/40 text-[9px] font-sans tracking-wider uppercase">{activeArticle.date}</span>
                  <span className="text-textGray/30 text-[9px] font-sans">·</span>
                  <span className="text-textGray/40 text-[9px] font-sans tracking-wider uppercase">{activeArticle.readTime}</span>
                </div>
                <h3 className="font-display text-[1.35rem] font-light text-charcoal italic leading-snug mb-3">
                  {activeArticle.title}
                </h3>
                <p className="text-textGray/70 text-[12px] font-sans font-light leading-[1.85] mb-4 border-l-2 border-bronze/30 pl-3 italic">
                  {activeArticle.excerpt}
                </p>
                <div className="h-[1px] bg-warmGray/15 mb-4" />
                <div className="space-y-3">
                  {activeArticle.body.split("\n\n").map((para, i) => (
                    <p key={i} className="text-charcoal text-[12px] font-sans font-light leading-[1.9]">{para}</p>
                  ))}
                </div>
                {/* Prev / Next */}
                <div className="flex items-center justify-between mt-8 pt-5 border-t border-warmGray/15">
                  <button
                    onClick={() => { const idx = articles.findIndex((a) => a.id === activeId); if (idx > 0) setActiveId(articles[idx - 1].id); }}
                    disabled={articles.findIndex((a) => a.id === activeId) === 0}
                    className="inline-flex items-center gap-1.5 text-[9px] font-sans font-semibold tracking-wider uppercase text-textGray/50 hover:text-bronze transition-colors duration-300 disabled:opacity-20 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-arrow-left-line text-xs"></i>
                    <span>Prev</span>
                  </button>
                  <div className="flex items-center gap-1.5">
                    {articles.map((a) => (
                      <button
                        key={a.id}
                        onClick={() => setActiveId(a.id)}
                        className={`rounded-full transition-all duration-300 cursor-pointer ${activeId === a.id ? "w-4 h-1.5 bg-bronze" : "w-1.5 h-1.5 bg-warmGray/30 hover:bg-bronze/40"}`}
                      />
                    ))}
                  </div>
                  <button
                    onClick={() => { const idx = articles.findIndex((a) => a.id === activeId); if (idx < articles.length - 1) setActiveId(articles[idx + 1].id); }}
                    disabled={articles.findIndex((a) => a.id === activeId) === articles.length - 1}
                    className="inline-flex items-center gap-1.5 text-[9px] font-sans font-semibold tracking-wider uppercase text-textGray/50 hover:text-bronze transition-colors duration-300 disabled:opacity-20 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                  >
                    <span>Next</span>
                    <i className="ri-arrow-right-line text-xs"></i>
                  </button>
                </div>
              </div>
            </div>

            {/* ── Desktop layout: side by side ── */}
            <div className="hidden md:flex gap-8 items-start">
              {/* Left nav */}
              <nav className="w-64 flex-shrink-0 sticky top-24">
                <ul className="space-y-1">
                  {articles.map((article, index) => {
                    const isActive = activeId === article.id;
                    return (
                      <li key={article.id}>
                        <button
                          onClick={() => setActiveId(article.id)}
                          className={`w-full text-left px-4 py-4 rounded-xl transition-all duration-300 cursor-pointer group ${
                            isActive
                              ? "bg-cream border border-bronze/20 shadow-card"
                              : "hover:bg-cream/60 border border-transparent"
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <span className={`text-[9px] font-sans font-semibold tracking-widest mt-0.5 transition-colors duration-300 ${isActive ? "text-bronze" : "text-textGray/30 group-hover:text-bronze/50"}`}>
                              {String(index + 1).padStart(2, "0")}
                            </span>
                            <div className="flex-1 min-w-0">
                              <span className={`block text-[8px] font-sans font-semibold tracking-[0.25em] uppercase mb-1 transition-colors duration-300 ${isActive ? "text-bronze" : "text-textGray/40 group-hover:text-bronze/60"}`}>
                                {article.category}
                              </span>
                              <span className={`block font-display text-[13px] font-light italic leading-snug transition-colors duration-300 ${isActive ? "text-charcoal" : "text-textGray/60 group-hover:text-charcoal/80"}`}>
                                {article.title}
                              </span>
                              <span className={`block text-[9px] font-sans mt-1.5 transition-colors duration-300 ${isActive ? "text-textGray/50" : "text-textGray/30"}`}>
                                {article.date}
                              </span>
                            </div>
                            {isActive && (
                              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <i className="ri-arrow-right-s-line text-bronze text-sm"></i>
                              </div>
                            )}
                          </div>
                        </button>
                        {index < articles.length - 1 && (
                          <div className="mx-4 h-[1px] bg-warmGray/10 mt-1" />
                        )}
                      </li>
                    );
                  })}
                </ul>
                <div className="mt-8 pt-6 border-t border-warmGray/15">
                  <p className="text-textGray/40 text-[9px] font-sans font-light leading-relaxed">
                    Clinical perspectives from the Vionne Stability Method practice.
                  </p>
                </div>
              </nav>

              {/* Vertical divider */}
              <div className="w-[1px] bg-warmGray/15 self-stretch flex-shrink-0" />

              {/* Right content */}
              <div className="flex-1 min-w-0">
                <div key={activeId} style={{ animation: "fadeSlideIn 0.4s ease-out" }}>
                  <div className="w-full h-[260px] overflow-hidden mb-8">
                    <img
                      src={activeArticle.image}
                      alt={activeArticle.title}
                      title={`${activeArticle.title} - Vionne Stability`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="px-3 py-1 rounded-full text-[8px] font-sans font-semibold tracking-wider uppercase bg-bronze/10 text-bronze border border-bronze/20">
                      {activeArticle.category}
                    </span>
                    <span className="text-textGray/40 text-[9px] font-sans tracking-wider uppercase">{activeArticle.date}</span>
                    <span className="text-textGray/30 text-[9px] font-sans">·</span>
                    <span className="text-textGray/40 text-[9px] font-sans tracking-wider uppercase">{activeArticle.readTime}</span>
                  </div>
                  <h3 className="font-display text-2xl md:text-[1.9rem] font-light text-charcoal italic leading-snug mb-4">
                    {activeArticle.title}
                  </h3>
                  <p className="text-textGray/70 text-[13px] font-sans font-light leading-[1.9] mb-6 border-l-2 border-bronze/30 pl-4 italic">
                    {activeArticle.excerpt}
                  </p>
                  <div className="h-[1px] bg-warmGray/15 mb-6" />
                  <div className="space-y-4">
                    {activeArticle.body.split("\n\n").map((para, i) => (
                      <p key={i} className="text-charcoal text-[12.5px] font-sans font-light leading-[2]">{para}</p>
                    ))}
                  </div>
                  <div className="flex items-center justify-between mt-10 pt-6 border-t border-warmGray/15">
                    <button
                      onClick={() => { const idx = articles.findIndex((a) => a.id === activeId); if (idx > 0) setActiveId(articles[idx - 1].id); }}
                      disabled={articles.findIndex((a) => a.id === activeId) === 0}
                      className="inline-flex items-center gap-2 text-[9px] font-sans font-semibold tracking-wider uppercase text-textGray/50 hover:text-bronze transition-colors duration-300 disabled:opacity-20 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-arrow-left-line text-xs"></i>
                      <span>Previous</span>
                    </button>
                    <div className="flex items-center gap-2">
                      {articles.map((a) => (
                        <button
                          key={a.id}
                          onClick={() => setActiveId(a.id)}
                          className={`rounded-full transition-all duration-300 cursor-pointer ${activeId === a.id ? "w-5 h-1.5 bg-bronze" : "w-1.5 h-1.5 bg-warmGray/30 hover:bg-bronze/40"}`}
                        />
                      ))}
                    </div>
                    <button
                      onClick={() => { const idx = articles.findIndex((a) => a.id === activeId); if (idx < articles.length - 1) setActiveId(articles[idx + 1].id); }}
                      disabled={articles.findIndex((a) => a.id === activeId) === articles.length - 1}
                      className="inline-flex items-center gap-2 text-[9px] font-sans font-semibold tracking-wider uppercase text-textGray/50 hover:text-bronze transition-colors duration-300 disabled:opacity-20 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                    >
                      <span>Next</span>
                      <i className="ri-arrow-right-line text-xs"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Masterclass ── */}
        {activeSection === "masterclass" && (
          <div className="max-w-3xl">
            <MasterclassContent />
          </div>
        )}

        {/* ── Training ── */}
        {activeSection === "training" && (
          <div className="max-w-3xl">
            <TrainingContent />
          </div>
        )}

      </div>

      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateX(12px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </section>
  );
};

export default BlogSection;
